import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import ExpenseDataValidation from "../helper/ExpenseDataValidation";
import { Icon } from "@iconify/react";
import { AddExpense, EditExpense } from "../data";
import { toast } from "react-toastify";
import { Languages } from "../Utils/Languages";
import BusinessDropdown from "./child/BusinessDropdown";
import Currancy from "../Utils/currency.json";
import CountryFlags from "../Utils/countries.json";
import { getImage } from "../Utils/image_helper";

const ExpenseEditLayer = ({ }) => {
    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();
    const expense_data = state?.EXPENSE?.find(expense => expense.expenseId == state?.SELECTEDEXPENSE);
    const [showDropdown, setShowDropdown] = useState(false);

    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
        if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
            setShowCurrencyDropdown(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);



    const [errors, setErrors] = useState({});
    const [expenseData, setExpenseData] = useState({
        companyId: expense_data?.companyId || state?.BUSINESSES[0]?.companyId,
        category: expense_data?.expenseCategory || "",
        amount: expense_data?.amount || "",
        expenseDate: expense_data?.expenseDate || new Date().getTime().toString(),
        description: expense_data?.expenseDescription || "",
        expenseName: expense_data?.expenseName || "",
        paymentMethod: expense_data?.paymentMethod || "Cash",
        expenseId: expense_data?.expenseId || Math.floor(new Date().getTime()),
        currency: expense_data?.currency || ""
    });
    const handleChangeExpense = (field, value) => {
        setExpenseData({ ...expenseData, [field]: value });
        // setCustomerError({ ...customerError, [field]: null });
    }

    const [currencySearch, setCurrencySearch] = useState("");
    const currencyDropdownRef = useRef(null);
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

    const currencyOptions = useMemo(() => {
        return Currancy.CountryCurrencyResponse.map(row => {
            const flagCountry = CountryFlags.CurrencyResponse?.find(c => c.isoAlpha2 === row.countryCode);
            return {
                label: `${row.name} (${row.currencyCode} - ${row.currencytSymbol})`,
                value: row.currencyCode,
                raw: row,
                flag: flagCountry?.flag
            };
        });
    }, []);

    const selectedCurrencyOption = useMemo(() => {
        return currencyOptions?.find(opt => opt.value === expenseData.currency);
    }, [currencyOptions, expenseData.currency]);

    const [filteredCurrencyOptions, setFilteredCurrencyOptions] = useState(currencyOptions);

    const handleCurrencySearch = (e) => {
        const value = e.target.value;
        setCurrencySearch(value);
        if (!value) {
            setFilteredCurrencyOptions(currencyOptions);
        } else {
            const filtered = currencyOptions.filter((opt) =>
                opt.label.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredCurrencyOptions(filtered);
        }
    };

    useEffect(() => {
        if (!expenseData.currency) {
            const selectedBusiness = state?.BUSINESSES?.find(b => String(b.companyId) === String(expenseData.companyId));
            const bizCurrency = selectedBusiness?.currency || state?.CURRENCYCODE || "";
            if (bizCurrency) {
                setExpenseData(prev => ({ ...prev, currency: bizCurrency }));
            }
        }
    }, [expenseData.companyId, state?.BUSINESSES, expenseData.currency]);


    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])].sort(
            (a, b) => Number(b.creationDate) - Number(a.creationDate)
        );
    }, [state?.BUSINESSES]);

    const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";
    const isProfessionalPlan = userPlanName === process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const isAgencyPlan = userPlanName === process.env.REACT_APP_AGENCY_PLAN_NAME;

    let allowedBusinessCount = 1;

    if (isProfessionalPlan) {
        allowedBusinessCount = 3;
    }

    if (isAgencyPlan) {
        allowedBusinessCount = sortedBusinesses.length;
    }


    const expenseCategories = [
        "Personal", "Stationary", "Staff Salary", "Donation", "Entertainment",
        "Miscellaneous", "Utility", "Office Furniture", "Electronics", "Food", "Other"
    ];

    const paymentModes = ["cash", "UPI", "cheque", "card", "electronics"].map(
        mode => Languages[state?.LANGUAGE]?.[mode] || mode
    );


    const handleSubmit = async () => {
        const { isValid, errors } = ExpenseDataValidation(expenseData);


        if (isValid) {

            const selectedBusiness = state?.BUSINESSES?.find(b => b.companyId === expenseData.companyId);
            const targetUid = selectedBusiness?.isShared ? selectedBusiness.ownerUid : state?.USERID;

            const { success, message } = await EditExpense(
                state,
                dispatch,
                expense_data.uniqueID,
                expenseData,
                targetUid
            );
            if (success && message == "Expense updated successfully!") {
                navigate(-1);
                toast.success(Languages[state?.LANGUAGE]?.Expense_edit_success_message);

            } else {
                toast.success(Languages[state?.LANGUAGE]?.Expense_edit_failed_message);

            }
        } else {
            setErrors(errors);
        }

    };



    return (
        <div className="card p-4 shadow-sm border-0">

            <div className="card-header">

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">

                    <div className="d-flex align-items-center gap-2">
                        <button
                            onClick={() => { navigate(-1) }}
                        >
                            <i class="fa fa-arrow-left" style={{ fontSize: 18 }}></i>
                        </button>
                        <p className='fw-semibold mb-0' style={{ fontSize: 20 }}> {Languages[state?.LANGUAGE]?.expense_edit}</p>
                    </div>

                    <div className="d-flex gap-2">
                        <BusinessDropdown
                            businesses={state?.BUSINESSES || []}
                            selectedCompanyId={expenseData.companyId}
                            onBusinessChange={() => {}} // Disabled in Edit mode
                            disabled={true}
                        />
                    </div>
                </div>

            </div>

            <div className="card-body">
                <div className="row">


                    {/* Expense Date */}
                    <div className="col-sm-12 col-md-6 mb-3">
                        <label className="form-label">{Languages[state?.LANGUAGE]?.Expense_Date}</label>
                        <input
                            type="date"
                            className="form-control wizard-required"
                            value={(() => {
                                try {
                                    return expenseData?.expenseDate
                                        ? new Date(parseInt(expenseData?.expenseDate)).toISOString().split('T')[0]
                                        : '';
                                } catch (e) {
                                    return '';
                                }
                            })()}
                            onChange={(e) =>
                                handleChangeExpense(
                                    "expenseDate",
                                    new Date(e.target.value).getTime().toString()
                                )
                            }
                        />
                    </div>

                    {/* Expense Category */}
                    <div className="col-md-6 mb-3">
                        <div style={{ width: "100%", position: "relative" }}>
                            <label className="form-label">
                                {Languages[state?.LANGUAGE]?.Expense_Category}  <span className="text-danger">*</span>
                            </label>

                            {/* Styled Input with Dropdown Button */}
                            <div className={`form-control d-flex align-items-center p-0 ${errors.category ? "border-danger" : ""}`}>
                                <div className="dropdown w-100" ref={dropdownRef}>
                                    <input
                                        type="text"
                                        className="form-control border-0 text-start"
                                        placeholder={Languages[state?.LANGUAGE]?.Expense_Category}
                                        value={expenseData.category}
                                        onChange={(e) => {
                                            handleChangeExpense("category", e.target.value);
                                            setShowDropdown(true); // Show dropdown when typing
                                        }}
                                        onFocus={() => setShowDropdown(true)} // Show dropdown on focus
                                        style={{
                                            background: "transparent",
                                            outline: "none",
                                            fontSize: 14,
                                            paddingLeft: 20,
                                            font: "inherit",
                                        }}
                                    />

                                    {/* Custom Dropdown Menu */}
                                    {showDropdown && (
                                        <ul
                                            className="dropdown-menu w-100 show"
                                            style={{ maxHeight: "250px", overflowY: "auto", padding: "10px" }}
                                        >
                                            {/* Filtered Category Options */}
                                            {expenseCategories
                                                .filter((category) =>
                                                    category.toLowerCase().includes(expenseData?.category.toLowerCase())
                                                )
                                                .map((category, index) => (
                                                    <li key={index}>
                                                        <button
                                                            className={`dropdown-item px-16 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${expenseData.category === category ? "bg-primary text-white" : ""
                                                                }`}
                                                            type="button"
                                                            style={{ height: "auto" }}
                                                            onClick={() => {
                                                                handleChangeExpense("category", category);
                                                                setShowDropdown(false); // Hide dropdown after selection
                                                            }}
                                                        >
                                                            {category}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/* Error Message */}
                            {errors.category && <p className="text-danger">{errors.category}</p>}
                        </div>
                    </div>


                    {/* Expense Name */}
                    <div className="col-md-6 mb-3">
                        <label className="form-label">{Languages[state?.LANGUAGE]?.Expense_Name} <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" placeholder="Enter Name" onChange={(e) =>
                            handleChangeExpense("expenseName", e.target.value)
                        }
                            value={expenseData.expenseName}
                        />
                        {errors.expenseName && <p className="text-danger">{errors.expenseName}</p>}

                    </div>

                    {/* Amount */}
                    <div className="col-md-6 mb-3">
                        <label className="form-label">{Languages[state?.LANGUAGE]?.amount} <span className="text-danger">*</span> </label>
                        <input type="number" className="form-control" placeholder="Enter amount" onChange={(e) =>
                            handleChangeExpense("amount", e.target.value)
                        }
                            value={expenseData.amount} />
                        {errors.amount && <p className="text-danger">{errors.amount}</p>}
                    </div>

                    {/* Country / Currency */}
                    <div className="col-md-6 mb-3">
                        <div style={{ width: "100%", position: "relative" }}>
                            <label className="form-label">
                                {Languages[state?.LANGUAGE]?.currency || "Currency"}
                            </label>
                            
                            {/* Dropdown with Button Inside */}
                            <div className="dropdown" ref={currencyDropdownRef}>
                                <button
                                    className="btn btn-outline-primary-600 dropdown-toggle w-100 d-flex justify-content-between align-items-center px-16 py-10 rounded"
                                    type="button"
                                    onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                    style={{ fontSize: 14 }}
                                >
                                    <span className="d-flex align-items-center gap-2">
                                        {selectedCurrencyOption ? (
                                            <>
                                                <img
                                                    src={
                                                        selectedCurrencyOption.flag
                                                            ? `data:image/png;base64,${selectedCurrencyOption.flag}`
                                                            : getImage("euro_flag")
                                                    }
                                                    alt="flag"
                                                    width="25"
                                                    height="15"
                                                    style={{ objectFit: "cover", borderRadius: 2 }}
                                                />
                                                <span>
                                                    {selectedCurrencyOption.raw?.name} ({selectedCurrencyOption.raw?.currencyCode} - {selectedCurrencyOption.raw?.currencytSymbol})
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <img
                                                    src={getImage("euro_flag")}
                                                    alt="flag"
                                                    width="25"
                                                    height="15"
                                                    style={{ objectFit: "cover", borderRadius: 2 }}
                                                />
                                                <span>Select Currency</span>
                                            </>
                                        )}
                                    </span>
                                    <i className="bi bi-chevron-down"></i>
                                </button>

                                {/* Dropdown Menu */}
                                {showCurrencyDropdown && (
                                    <ul
                                        className="dropdown-menu w-100 show mt-1 shadow-sm border-0 rounded-3 hide-scrollbar"
                                        style={{ padding: 0, zIndex: 1050 }}
                                    >
                                        <li className="p-2 sticky-top bg-white z-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search country..."
                                                value={currencySearch}
                                                onChange={handleCurrencySearch}
                                                autoFocus
                                            />
                                        </li>

                                        <div
                                            style={{ maxHeight: "250px", overflowY: "auto" }}
                                            className="hide-scrollbar"
                                        >
                                            {filteredCurrencyOptions.length > 0 ? (
                                                filteredCurrencyOptions.map((option) => (
                                                    <li key={`${option.raw.countryCode}-${option.value}`}>
                                                        <button
                                                            className={`dropdown-item d-flex align-items-center gap-2 px-16 py-10 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${
                                                                selectedCurrencyOption?.value === option.value
                                                                    ? "bg-primary-50 fw-semibold text-primary-600"
                                                                    : ""
                                                            }`}
                                                            type="button"
                                                            onClick={() => {
                                                                handleChangeExpense("currency", option.value);
                                                                setCurrencySearch("");
                                                                setShowCurrencyDropdown(false);
                                                            }}
                                                        >
                                                            <img
                                                                src={
                                                                    option.flag
                                                                        ? `data:image/png;base64,${option.flag}`
                                                                        : getImage("euro_flag")
                                                                }
                                                                alt="flag"
                                                                width="25"
                                                                height="15"
                                                                style={{
                                                                    objectFit: "cover",
                                                                    borderRadius: 2,
                                                                }}
                                                            />
                                                            {option.label}
                                                        </button>
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="text-muted text-center small py-2">
                                                    No Country found
                                                </li>
                                            )}
                                        </div>
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Payment Mode */}
                    <div className="col-md-6 mb-3">
                        <div style={{ width: "100%", position: "relative" }}>
                            <label className="form-label">
                                {Languages[state?.LANGUAGE]?.Payment_Method}
                            </label>

                            {/* Dropdown with Button Inside */}
                            <div className="form-control d-flex align-items-center p-0">
                                <div className="dropdown w-100">
                                    <button
                                        className="form-control border-0 text-start"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{
                                            background: "transparent",
                                            outline: "none",
                                            fontSize: 14,
                                            paddingLeft: 20,
                                            font: "inherit",
                                        }}
                                    >
                                        {expenseData.paymentMethod
                                            ? expenseData.paymentMethod.charAt(0).toUpperCase() + expenseData.paymentMethod.slice(1)
                                            : Languages[state?.LANGUAGE]?.cash}
                                    </button>

                                    {/* Dropdown Menu */}
                                    <ul
                                        className="dropdown-menu w-100"
                                        style={{ maxHeight: "300px", overflowY: "auto", padding: "10px" }}
                                    >
                                        {paymentModes.map((mode, index) => (
                                            <li key={index}>
                                                <button
                                                    className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                                    type="button"
                                                    onClick={() => handleChangeExpense("paymentMethod", mode)}
                                                >
                                                    {mode}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Description */}
                    <div className="col-12 mb-3">
                        <label className="form-label">{Languages[state?.LANGUAGE]?.description}</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Enter details..."
                            value={expenseData.description} // Correct placement
                            onChange={(e) => handleChangeExpense("description", e.target.value)} // Corrected syntax
                        ></textarea>

                    </div>
                </div>

                {/* Buttons */}
                <div
                    className="d-flex gap-2 justify-content-end mt-3"
                >
                    <button
                        type="reset"
                        onClick={() => {
                            navigate(-1);
                        }}
                        className="border btn-sm border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-6 radius-8"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center px-40 py-6 radius-8 gap-2"
                        onClick={handleSubmit}
                    >
                        <Icon icon="simple-line-icons:check" className="text-xl" />
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExpenseEditLayer;
